import React, { useState } from 'react';
import { Card, Row, Col, Button, Tag, Select, Table, Tabs, Modal, Input, Radio } from 'antd';
import { Chart, Geom, Axis, Tooltip, Legend, Coord, Label } from 'bizcharts';
import DataSet from '@antv/data-set';
import { StyledCard, SectionTitle } from './QualityDashboard.styles';

const { Option } = Select;

// Colour coding for listing/review metrics (Uplisting)
// 5-star metrics (Average Review Score 0–5): ≥4.7 Green, 4.6–<4.7 Orange, <4.6 Red
function getScoreColor(score) {
  if (score >= 4.7) return '#52c41a';
  if (score >= 4.6) return '#fa8c16';
  return '#ff4d4f';
}
// 5-Star Review Rate (%): ≥40 Green, 30–40 Orange, <30 Red
function getFiveStarRateColor(pct) {
  if (pct >= 40) return '#52c41a';
  if (pct >= 30) return '#fa8c16';
  return '#ff4d4f';
}

// Trends chart data: weekly average review score and 5-star rate (YoY comparison)
const weeklyScoreData = [
  { week: 'W1', score: 4.52, scoreYoY: 4.48 },
  { week: 'W2', score: 4.54, scoreYoY: 4.5 },
  { week: 'W3', score: 4.51, scoreYoY: 4.49 },
  { week: 'W4', score: 4.55, scoreYoY: 4.51 },
  { week: 'W5', score: 4.53, scoreYoY: 4.5 },
  { week: 'W6', score: 4.56, scoreYoY: 4.52 },
  { week: 'W7', score: 4.54, scoreYoY: 4.51 },
  { week: 'W8', score: 4.57, scoreYoY: 4.53 },
  { week: 'W9', score: 4.55, scoreYoY: 4.52 },
  { week: 'W10', score: 4.58, scoreYoY: 4.54 },
  { week: 'W11', score: 4.56, scoreYoY: 4.53 },
  { week: 'W12', score: 4.59, scoreYoY: 4.55 }
];
const weeklyFiveStarData = [
  { week: 'W1', rate: 0.26, rateYoY: 0.24 },
  { week: 'W2', rate: 0.28, rateYoY: 0.25 },
  { week: 'W3', rate: 0.27, rateYoY: 0.26 },
  { week: 'W4', rate: 0.29, rateYoY: 0.27 },
  { week: 'W5', rate: 0.28, rateYoY: 0.26 },
  { week: 'W6', rate: 0.3, rateYoY: 0.28 },
  { week: 'W7', rate: 0.29, rateYoY: 0.27 },
  { week: 'W8', rate: 0.31, rateYoY: 0.29 },
  { week: 'W9', rate: 0.3, rateYoY: 0.28 },
  { week: 'W10', rate: 0.32, rateYoY: 0.3 },
  { week: 'W11', rate: 0.31, rateYoY: 0.29 },
  { week: 'W12', rate: 0.33, rateYoY: 0.31 }
];

// Header KPIs (Uplisting: Latest Updates)
const headerKpis = {
  avgScore: { current: 4.82, lastMonth: 4.79, mom: 0.03, yoy: 0.05 },
  fiveStarPct: { current: 58, lastMonth: 55, mom: 3, yoy: 8 },
  goodStanding: { count: 4, total: 5 },
  warningsAtRisk: { count: 1, mom: 0, yoy: -1 }
};

// Average Scores and Components (6 components, 0-5)
const componentScores = [
  { key: 'cleanliness', label: 'Cleanliness', score: 4.7 },
  { key: 'accuracy', label: 'Accuracy', score: 4.8 },
  { key: 'communication', label: 'Communication', score: 4.9 },
  { key: 'checkin', label: 'Check-in', score: 4.6 },
  { key: 'location', label: 'Location', score: 4.85 },
  { key: 'value', label: 'Value', score: 4.5 }
];

// Component Evolution: dummy weekly rate per category (0–1, like review component scores over time)
const componentEvolutionColors = {
  Cleanliness: '#13C2C2',
  Accuracy: '#FA8C16',
  Communication: '#595959',
  'Check-in': '#69C0FF',
  Location: '#FAAD14',
  Value: '#2F54EB'
};
const componentEvolutionData = [
  { week: 'W1', Cleanliness: 0.92, Accuracy: 0.94, Communication: 0.96, 'Check-in': 0.88, Location: 0.96, Value: 0.88 },
  { week: 'W2', Cleanliness: 0.91, Accuracy: 0.95, Communication: 0.96, 'Check-in': 0.89, Location: 0.96, Value: 0.88 },
  { week: 'W3', Cleanliness: 0.92, Accuracy: 0.94, Communication: 0.97, 'Check-in': 0.88, Location: 0.97, Value: 0.89 },
  { week: 'W4', Cleanliness: 0.93, Accuracy: 0.95, Communication: 0.96, 'Check-in': 0.9, Location: 0.96, Value: 0.9 },
  { week: 'W5', Cleanliness: 0.92, Accuracy: 0.96, Communication: 0.97, 'Check-in': 0.89, Location: 0.97, Value: 0.89 },
  { week: 'W6', Cleanliness: 0.93, Accuracy: 0.95, Communication: 0.96, 'Check-in': 0.9, Location: 0.97, Value: 0.9 },
  { week: 'W7', Cleanliness: 0.92, Accuracy: 0.96, Communication: 0.97, 'Check-in': 0.9, Location: 0.96, Value: 0.9 },
  { week: 'W8', Cleanliness: 0.94, Accuracy: 0.95, Communication: 0.97, 'Check-in': 0.91, Location: 0.97, Value: 0.91 },
  { week: 'W9', Cleanliness: 0.93, Accuracy: 0.96, Communication: 0.97, 'Check-in': 0.9, Location: 0.97, Value: 0.9 },
  { week: 'W10', Cleanliness: 0.94, Accuracy: 0.96, Communication: 0.98, 'Check-in': 0.91, Location: 0.98, Value: 0.91 },
  { week: 'W11', Cleanliness: 0.93, Accuracy: 0.95, Communication: 0.97, 'Check-in': 0.91, Location: 0.97, Value: 0.91 },
  { week: 'W12', Cleanliness: 0.94, Accuracy: 0.96, Communication: 0.98, 'Check-in': 0.92, Location: 0.98, Value: 0.92 }
];

// Quality Overview – KPI values and Quality Status by Listing (same structure as Demo 4 accommodationStatuses)
const qualityOverview = {
  guestFavorites: { count: 2, total: 5 },
  qualityPercentile: 72,
  pendingOpportunities: 3,
  totalListings: 5,
  missingCheckoutTasks: 1
};
const qualityStatusByListing = [
  { label: 'Good', count: 0, color: '#52c41a' },
  { label: 'Educate', count: 57, color: '#faad14' },
  { label: 'Warn', count: 200, color: '#fa8c16' },
  { label: 'Probation', count: 72, color: '#fa541c' },
  { label: 'Additional warn', count: 9, color: '#fa8c16' },
  { label: 'Pending removal', count: 5, color: '#ff4d4f' },
  { label: 'Suspend', count: 3, color: '#ff4d4f' },
  { label: 'Removed', count: 0, color: '#ff4d4f' }
];

// Cancellation Policy: all policy types with dummy counts (for tags + chart)
const cancellationPolicyData = [
  { policy: 'Flexible', count: 35 },
  { policy: 'Moderate', count: 20 },
  { policy: 'Firm', count: 15 },
  { policy: 'Strict', count: 2 },
  { policy: 'Super Strict 30 Days', count: 1 },
  { policy: 'Super Strict 60 Days', count: 0 },
  { policy: 'Non-Refundable', count: 50 },
  { policy: 'Limited', count: 30 }
];
// Chart colors per policy type (for donut)
const cancellationPolicyColors = {
  Flexible: '#52c41a',
  Moderate: '#1890ff',
  Firm: '#faad14',
  Strict: '#fa8c16',
  'Super Strict 30 Days': '#eb2f96',
  'Super Strict 60 Days': '#531dab',
  'Non-Refundable': '#ff4d4f',
  Limited: '#8c8c8c'
};

// Quality Percentile - Top and Bottom Listings (dummy)
const topListingsDummy = [
  { name: 'Seaside Apartment', city: 'London', qualityPercentile: 92 },
  { name: 'Garden View Studio', city: 'Edinburgh', qualityPercentile: 88 }
];
const bottomListingsDummy = [
  { name: 'City Loft', city: 'Manchester', qualityPercentile: 42 },
  { name: 'Riverside Flat', city: 'Birmingham', qualityPercentile: 38 }
];

// Listing Deep Dive dummy
const listingDeepDiveData = [
  {
    key: '1',
    name: 'Seaside Apartment',
    host: 'John',
    country: 'UK',
    city: 'London',
    overall: 4.82,
    cleanliness: 4.8,
    accuracy: 4.9,
    communication: 4.7,
    checkin: 4.6,
    location: 4.9,
    value: 4.5,
    fiveStarPct: 58,
    qualityStatus: 'Good',
    qualityPercentile: 78
  },
  {
    key: '2',
    name: 'City Loft',
    host: 'Jane',
    country: 'UK',
    city: 'Manchester',
    overall: 4.65,
    cleanliness: 4.5,
    accuracy: 4.7,
    communication: 4.8,
    checkin: 4.6,
    location: 4.9,
    value: 4.4,
    fiveStarPct: 45,
    qualityStatus: 'Educate',
    qualityPercentile: 62
  }
];

const QualityDashboardDemo6 = () => {
  const [granularity, setGranularity] = useState('weekly');
  const [lookback, setLookback] = useState('3');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [deepDiveTab, setDeepDiveTab] = useState('listing');

  // Transform weekly data for Trends charts (fold Current vs YoY)
  const dsScore = new DataSet();
  const dvScore = dsScore.createView().source(weeklyScoreData);
  dvScore.transform({ type: 'fold', fields: ['score', 'scoreYoY'], key: 'type', value: 'value' }).transform({
    type: 'map',
    callback: row => ({ ...row, type: row.type === 'score' ? 'This Period' : 'YoY' })
  });
  const dsFiveStar = new DataSet();
  const dvFiveStar = dsFiveStar.createView().source(weeklyFiveStarData);
  dvFiveStar.transform({ type: 'fold', fields: ['rate', 'rateYoY'], key: 'type', value: 'value' }).transform({
    type: 'map',
    callback: row => ({
      ...row,
      type: row.type === 'rate' ? 'This Period' : 'YoY',
      value: Number((row.value * 100).toFixed(1))
    })
  });

  const componentEvolutionFields = ['Cleanliness', 'Accuracy', 'Communication', 'Check-in', 'Location', 'Value'];
  const dsComponent = new DataSet();
  const dvComponent = dsComponent.createView().source(componentEvolutionData);
  dvComponent.transform({
    type: 'fold',
    fields: componentEvolutionFields,
    key: 'category',
    value: 'rate'
  });

  const qualityPieData = qualityStatusByListing.filter(s => s.count > 0).map(s => ({ type: s.label, count: s.count, color: s.color }));
  const { DataView } = DataSet;
  const qualityDv = new DataView();
  qualityDv.source(qualityPieData).transform({
    type: 'percent',
    field: 'count',
    dimension: 'type',
    as: 'percent'
  });
  const qualityCols = { percent: { formatter: val => (Number(val) * 100).toFixed(2) + '%' } };

  const cancellationBarData = cancellationPolicyData.map(p => ({ type: p.policy, count: p.count }));
  const nonRefundableApplied = cancellationPolicyData.find(p => p.policy === 'Non-Refundable');
  const nonRefundableCount = nonRefundableApplied ? nonRefundableApplied.count : 0;
  const totalListingsPolicy = cancellationPolicyData.reduce((sum, p) => sum + p.count, 0);

  return (
    <StyledCard
      title={
        <span>
          Airbnb Quality Dashboard (Demo 6 — Uplisting style){' '}
          <a
            href="https://support.uplisting.io/docs/airbnb-quality-dashboard"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}
          >
            Reference Link - Uplisting Docs
          </a>
        </span>
      }
    >
      {/* Sticky filter panel */}
      <div
        style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', padding: '12px 0', borderBottom: '1px solid #f0f0f0', marginBottom: 24 }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          <Button icon="filter" onClick={() => setFiltersVisible(true)}>
            Filters
          </Button>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Granularity:</span>
          <Select value={granularity} onChange={setGranularity} style={{ width: 120 }} size="small">
            <Option value="weekly">Weekly</Option>
            <Option value="monthly">Monthly</Option>
          </Select>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Lookback:</span>
          <Select value={lookback} onChange={setLookback} style={{ width: 120 }} size="small">
            <Option value="1">1 month</Option>
            <Option value="3">3 months</Option>
            <Option value="6">6 months</Option>
            <Option value="12">1 year</Option>
            <Option value="24">2 years</Option>
            <Option value="all">All time</Option>
          </Select>
          <Button type="link" size="small" style={{ marginLeft: 'auto' }}>
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Latest Updates */}
      <SectionTitle style={{ marginBottom: 16 }}>Latest Updates</SectionTitle>
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Average Review Score (0–5)</div>
            <div style={{ fontSize: 24, fontWeight: 600, color: getScoreColor(headerKpis.avgScore.current) }}>{headerKpis.avgScore.current}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              Last month: {headerKpis.avgScore.lastMonth} · MoM: +{headerKpis.avgScore.mom} · YoY: +{headerKpis.avgScore.yoy}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>5-Star Review Rate (%)</div>
            <div style={{ fontSize: 24, fontWeight: 600, color: getFiveStarRateColor(headerKpis.fiveStarPct.current) }}>
              {headerKpis.fiveStarPct.current}%
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              Last month: {headerKpis.fiveStarPct.lastMonth}% · MoM: +{headerKpis.fiveStarPct.mom} · YoY: +{headerKpis.fiveStarPct.yoy}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Listings in Good Standing</div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>
              {headerKpis.goodStanding.count} / {headerKpis.goodStanding.total}
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Good or Educate status</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Listings with Warnings or At Risk</div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>{headerKpis.warningsAtRisk.count}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              MoM: {headerKpis.warningsAtRisk.mom} · YoY: {headerKpis.warningsAtRisk.yoy}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Reviews & Ratings — Average Scores and Components */}
      <SectionTitle style={{ marginBottom: 16 }}>Average Scores and Components</SectionTitle>
      <p style={{ marginBottom: 12, fontSize: 13, color: '#595959' }}>
        Six key quality components (1–5). Use the Lookback filter above to choose how far back data is pulled.
      </p>
      <Row gutter={[12, 12]} style={{ marginBottom: 32 }}>
        {componentScores.map(c => (
          <Col xs={24} sm={12} md={8} key={c.key}>
            <Card size="small" style={{ height: '100%' }}>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{c.label}</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: getScoreColor(c.score) }}>{c.score}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Trends – filters + two charts side by side (Uplisting screenshot) */}
      <SectionTitle style={{ marginBottom: 16 }}>Trends</SectionTitle>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <span style={{ fontSize: 13, color: '#595959' }}>Granularity:</span>
        <Radio.Group value={granularity} onChange={e => setGranularity(e.target.value)} size="small" buttonStyle="solid" style={{ marginRight: 24 }}>
          <Radio.Button value="weekly">Weekly</Radio.Button>
          <Radio.Button value="monthly">Monthly</Radio.Button>
        </Radio.Group>
        <span style={{ fontSize: 13, color: '#595959' }}>Lookback:</span>
        <Select value={lookback} onChange={setLookback} style={{ width: 140 }} size="small">
          <Option value="1">1 month</Option>
          <Option value="3">3 months</Option>
          <Option value="6">6 months</Option>
          <Option value="12">1 year</Option>
          <Option value="24">2 years</Option>
          <Option value="all">All time</Option>
        </Select>
      </div>
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        <Col xs={24} lg={12}>
          <Card title="Average Review Score" size="small" style={{ borderRadius: 8 }}>
            <Chart height={280} data={dvScore} forceFit>
              <Legend />
              <Axis name="week" />
              <Axis name="value" />
              <Tooltip />
              <Geom type="line" position="week*value" color="type" size={2} shape="smooth" />
              <Geom type="point" position="week*value" color="type" size={3} shape="circle" />
            </Chart>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="5-Star Review %" size="small" style={{ borderRadius: 8 }}>
            <Chart height={280} data={dvFiveStar} forceFit>
              <Legend />
              <Axis name="week" />
              <Axis name="value" label={{ formatter: v => `${v}%` }} />
              <Tooltip formatter={v => `${v}%`} />
              <Geom type="line" position="week*value" color="type" size={2} shape="smooth" />
              <Geom type="point" position="week*value" color="type" size={3} shape="circle" />
            </Chart>
          </Card>
        </Col>
      </Row>

      {/* Component Evolution – same filters as Trends, line chart with 6 categories and colour legend at bottom */}
      <SectionTitle style={{ marginBottom: 16 }}>Component Evolution</SectionTitle>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <span style={{ fontSize: 13, color: '#595959' }}>Granularity:</span>
        <Radio.Group value={granularity} onChange={e => setGranularity(e.target.value)} size="small" buttonStyle="solid" style={{ marginRight: 24 }}>
          <Radio.Button value="weekly">Weekly</Radio.Button>
          <Radio.Button value="monthly">Monthly</Radio.Button>
        </Radio.Group>
        <span style={{ fontSize: 13, color: '#595959' }}>Lookback:</span>
        <Select value={lookback} onChange={setLookback} style={{ width: 140 }} size="small">
          <Option value="1">1 month</Option>
          <Option value="3">3 months</Option>
          <Option value="6">6 months</Option>
          <Option value="12">1 year</Option>
          <Option value="24">2 years</Option>
          <Option value="all">All time</Option>
        </Select>
      </div>
      <Card
        title={`Component Evolution (${granularity === 'weekly' ? 'Weekly' : 'Monthly'})`}
        size="small"
        style={{ marginBottom: 32, borderRadius: 8 }}
      >
        <Chart height={320} data={dvComponent} forceFit>
          <Axis name="week" />
          <Axis name="rate" title={{ text: 'Rate' }} />
          <Tooltip />
          <Geom type="line" position="week*rate" color={['category', cat => componentEvolutionColors[cat] || '#999']} size={2} shape="smooth" />
          <Geom type="point" position="week*rate" color={['category', cat => componentEvolutionColors[cat] || '#999']} size={3} shape="circle" />
          <Legend position="bottom" visible={false} />
        </Chart>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '16px 24px',
            marginTop: 16,
            paddingTop: 12,
            borderTop: '1px solid #f0f0f0'
          }}
        >
          {componentEvolutionFields.map(cat => (
            <span key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#262626' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: componentEvolutionColors[cat], flexShrink: 0 }} />
              {cat}
            </span>
          ))}
        </div>
      </Card>

      {/* Quality Overview – screenshot layout: left = Quality Status by Listing chart, right = 4 KPIs; then Top/Bottom Listings */}
      <SectionTitle style={{ marginBottom: 16 }}>Quality Overview</SectionTitle>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card size="small" title="Quality Status by Listing" style={{ borderRadius: 8, minHeight: 420 }}>
            {qualityPieData.length > 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', minHeight: 380, padding: '8px 0' }}>
                <div style={{ width: '100%', maxWidth: 420, height: 380 }}>
                  <Chart data={qualityDv} scale={qualityCols} forceFit height={380} padding={[0, 0, 0, 0]}>
                    <Coord type="theta" radius={0.95} innerRadius={0.65} />
                    <Axis name="percent" />
                    <Geom
                      type="intervalStack"
                      position="percent"
                      color={[
                        'type',
                        type => {
                          const match = qualityPieData.find(d => d.type === type);
                          return (match && match.color) || '#999';
                        }
                      ]}
                      style={{ lineWidth: 1, stroke: '#fff' }}
                      tooltip={[
                        'type*percent',
                        (type, percent) => {
                          const match = qualityPieData.find(d => d.type === type);
                          return { name: type, value: match && match.count != null ? match.count : 0 };
                        }
                      ]}
                    >
                      <Label content="percent" formatter={(val, item) => item.point.type} />
                    </Geom>
                    <Tooltip showTitle={false} />
                  </Chart>
                </div>
              </div>
            ) : (
              <div style={{ minHeight: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c', fontSize: 14 }}>
                No data found
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card size="small" style={{ borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Guest Favorite</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>
                {qualityOverview.guestFavorites.count}/{qualityOverview.guestFavorites.total}
              </div>
            </Card>
            <Card size="small" style={{ borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Median Quality Percentile</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{qualityOverview.qualityPercentile}</div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Scale 0–100</div>
            </Card>
            <Card size="small" style={{ borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>With Pending Opportunities</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20, fontWeight: 600 }}>
                  {qualityOverview.pendingOpportunities}/{qualityOverview.totalListings}
                </span>
                <span style={{ fontSize: 14, color: '#8c8c8c', cursor: 'pointer' }} title="View details">
                  ↗
                </span>
              </div>
            </Card>
            <Card size="small" style={{ borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Missing Check-out Tasks</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{qualityOverview.missingCheckoutTasks}</div>
            </Card>
          </div>
        </Col>
      </Row>
      <Card size="small" title="Quality Percentile - Top and Bottom Listings" style={{ marginBottom: 32, borderRadius: 8 }}>
        <p style={{ fontSize: 13, color: '#595959', marginBottom: 16 }}>
          This section highlights performance leaders and opportunities for improvement. Top listings are your highest-performing properties by
          quality percentile; bottom listings are the lowest and are strong opportunities for targeted improvements.
        </p>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Top Listings</div>
            <div style={{ background: '#fafafa', borderRadius: 6, padding: 12 }}>
              {topListingsDummy.map((listing, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: idx < topListingsDummy.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{listing.name}</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{listing.city}</div>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#52c41a' }}>{listing.qualityPercentile}</span>
                </div>
              ))}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Bottom Listings</div>
            <div style={{ background: '#fafafa', borderRadius: 6, padding: 12 }}>
              {bottomListingsDummy.map((listing, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: idx < bottomListingsDummy.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{listing.name}</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{listing.city}</div>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#ff4d4f' }}>{listing.qualityPercentile}</span>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Card>

      {/* Cancellation Policy */}
      <SectionTitle style={{ marginBottom: 16 }}>Cancellation Policies</SectionTitle>
      <p style={{ marginBottom: 16, fontSize: 13, color: '#595959' }}>
        Breakdown by policy type. Consider more flexible policies to improve conversion.
      </p>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card size="small" title="Cancellation Policies (Listings)" style={{ borderRadius: 8, minHeight: 320 }}>
            {cancellationBarData.length > 0 ? (
              <div style={{ width: '100%', minHeight: 280, padding: '8px 0' }}>
                <Chart
                  data={cancellationBarData}
                  scale={{ type: { type: 'cat' }, count: { min: 0 } }}
                  forceFit
                  height={280}
                  padding={[16, 24, 32, 200]}
                >
                  <Coord transpose />
                  <Axis name="type" title={false} tickLine={false} label={{ offset: 8 }} />
                  <Axis name="count" title={false} />
                  <Geom
                    type="interval"
                    position="type*count"
                    color={['type', type => cancellationPolicyColors[type] || '#999']}
                    tooltip={['type*count', (type, count) => ({ name: type, value: count })]}
                  />
                  <Tooltip showTitle={false} />
                </Chart>
              </div>
            ) : (
              <div style={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c', fontSize: 14 }}>
                No data found
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card size="small" style={{ borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Non-Refundable Discount Applied</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>
                {nonRefundableCount}/{totalListingsPolicy}
              </div>
            </Card>
            <Card size="small" style={{ borderRadius: 8, border: '1px solid #52c41a', background: '#f6ffed' }}>
              <div style={{ fontSize: 13, color: '#262626' }}>
                <strong>Balanced.</strong> Your cancellation policies are well-balanced, offering a good mix of flexibility and protection.
              </div>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Deep Dive tables */}
      <SectionTitle style={{ marginBottom: 16 }}>Deep Dive Tables</SectionTitle>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#595959' }}>
          Listing, Booking, Opportunities, Notifications, Resolutions. Sortable columns, CSV/Excel export.
        </span>
        <Button type="primary" icon="download">
          Download report
        </Button>
      </div>
      <Tabs activeKey={deepDiveTab} onChange={setDeepDiveTab}>
        <Tabs.TabPane tab="Listing Deep Dive" key="listing">
          <Table
            size="small"
            dataSource={listingDeepDiveData}
            columns={[
              { title: 'Listing', dataIndex: 'name', key: 'name' },
              { title: 'Host', dataIndex: 'host', key: 'host' },
              { title: 'Country', dataIndex: 'country', key: 'country' },
              { title: 'City', dataIndex: 'city', key: 'city' },
              { title: 'Overall', dataIndex: 'overall', key: 'overall' },
              { title: '5-Star %', dataIndex: 'fiveStarPct', key: 'fiveStarPct', render: v => v + '%' },
              { title: 'Quality Status', dataIndex: 'qualityStatus', key: 'qualityStatus' },
              { title: 'Quality %', dataIndex: 'qualityPercentile', key: 'qualityPercentile' }
            ]}
            pagination={{ pageSize: 10 }}
            style={{ marginBottom: 32 }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Booking Deep Dive" key="booking">
          <Card
            size="small"
            style={{ marginBottom: 32, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bfbfbf' }}
          >
            [Booking-level table]
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Opportunities" key="opportunities">
          <Card
            size="small"
            style={{ marginBottom: 32, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bfbfbf' }}
          >
            [Opportunities table]
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Notifications" key="notifications">
          <Card
            size="small"
            style={{ marginBottom: 32, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bfbfbf' }}
          >
            [Notifications table]
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Resolutions" key="resolutions">
          <Card
            size="small"
            style={{ marginBottom: 32, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bfbfbf' }}
          >
            [Resolutions table]
          </Card>
        </Tabs.TabPane>
      </Tabs>

      {/* Filters modal */}
      <Modal title="Filters" visible={filtersVisible} onCancel={() => setFiltersVisible(false)} onOk={() => setFiltersVisible(false)} width={520}>
        <p style={{ marginBottom: 16, fontSize: 13, color: '#595959' }}>
          Multi-select: Country, City, Client, Property, Latest Quality Status, Cancellation Policy. Single-select: Has Recent Trip Issues (All / Yes
          / No).
        </p>
        <div style={{ marginBottom: 12 }}>
          <strong>Country</strong> <Input placeholder="Select" style={{ width: '100%', marginTop: 4 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <strong>City</strong> <Input placeholder="Select" style={{ width: '100%', marginTop: 4 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <strong>Client / Host</strong> <Input placeholder="Select" style={{ width: '100%', marginTop: 4 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <strong>Property</strong> <Input placeholder="Select" style={{ width: '100%', marginTop: 4 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <strong>Has Recent Trip Issues</strong>{' '}
          <Select style={{ width: '100%', marginTop: 4 }}>
            <Option value="all">All</Option>
            <Option value="yes">Yes</Option>
            <Option value="no">No</Option>
          </Select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <strong>Quality State</strong> <Input placeholder="Select" style={{ width: '100%', marginTop: 4 }} />
        </div>
        <div>
          <strong>Cancellation Policy</strong> <Input placeholder="Select" style={{ width: '100%', marginTop: 4 }} />
        </div>
      </Modal>
    </StyledCard>
  );
};

export default QualityDashboardDemo6;
